import { RouterStore } from 'mobx-react-router';
import { computed, observable, observe } from 'mobx';
import { HomePage, HomePageParams, homeRoute } from './routes/homeRoute';
import { ForYouPage, ForYouPageParams, forYouRoute } from './routes/forYouRoute';
import { CategoryPage, CategoryPageParams, categoryRoute } from './routes/categoryRoute';
import { SubCategory, SubCategoryPageParams, subCategoryRoute } from './routes/subCategoryRoute';
import { SearchPage, SearchPageParams, searchRoute } from './routes/searchRoute';
import { RouteLocation } from './routes/createRoute';
import { FilterCollection, EMPTY_FILTERS, isLocationsSame } from './routes/sortFiltersParams';

export interface RoutingStoreState {
  primaryCategorySlug: string;
}

interface Matches {
  home: HomePageParams | null;
  forYou: ForYouPageParams | null;
  category: CategoryPageParams | null;
  subCategory: SubCategoryPageParams | null;
  search: SearchPageParams | null;
}

interface LocationBuilders {
  home: typeof homeRoute.build;
  forYou: typeof forYouRoute.build;
  category: typeof categoryRoute.build;
  subCategory: typeof subCategoryRoute.build;
  search: typeof searchRoute.build;
}

interface BuildParams {
  page?: number;
  sortCategorySlug?: string | null;
  filters?: FilterCollection;
}

type BuildRouteParams = BuildParams &
  ({} | { criteria: string } | { categorySlug: string; subCategorySlug?: string | null });

export type MatchedRoute = HomePage | ForYouPage | CategoryPage | SubCategory | SearchPage;

const getLocationMatches = (location: RouteLocation): Matches => {
  if (!location) {
    return {
      home: null,
      forYou: null,
      category: null,
      subCategory: null,
      search: null,
    };
  }

  return {
    home: homeRoute.match(location),
    forYou: forYouRoute.match(location),
    category: categoryRoute.match(location),
    subCategory: subCategoryRoute.match(location),
    search: searchRoute.match(location),
  };
};

const getMatchedRoute = (matches: Matches): MatchedRoute => {
  if (matches.category) {
    return {
      routeName: 'category',
      params: matches.category,
    };
  }
  if (matches.subCategory) {
    return {
      routeName: 'subCategory',
      params: matches.subCategory,
    };
  }
  if (matches.search) {
    return {
      routeName: 'search',
      params: matches.search,
    };
  }
  if (matches.forYou) {
    return {
      routeName: 'forYou',
      params: matches.forYou || {},
    };
  }
  return {
    routeName: 'home',
    params: matches.home || {},
  };
};

export class RoutingStore extends RouterStore {
  public readonly primaryCategorySlug: string;

  @observable
  public previousLocation: RouteLocation | null = null;

  constructor({ primaryCategorySlug }: RoutingStoreState) {
    super();
    this.primaryCategorySlug = primaryCategorySlug;
    observe(this, 'location', (changes) => {
      if (changes.type === 'update') {
        this.previousLocation = changes.oldValue;
      }
    });
  }

  serialize(): RoutingStoreState {
    return {
      primaryCategorySlug: this.primaryCategorySlug,
    };
  }

  @computed
  public get matchedRoute(): MatchedRoute {
    return getMatchedRoute(getLocationMatches(this.location));
  }

  @computed
  public get previousMatchedRoute(): MatchedRoute | null {
    return this.previousLocation ? getMatchedRoute(getLocationMatches(this.previousLocation)) : null;
  }

  public rebuildRoute(params: Partial<BuildRouteParams>): RouteLocation {
    return this.buildRoute({
      criteria: '',
      categorySlug: this.primaryCategorySlug,
      subCategorySlug: null,
      ...this.matchedRoute.params,
      ...params,
    });
  }

  public buildRoute(params: BuildRouteParams): RouteLocation {
    const defaultParams = {
      criteria: '',
      categorySlug: this.primaryCategorySlug,
      subCategorySlug: null,
      page: 1,
      sortCategorySlug: null,
      filters: EMPTY_FILTERS,
      ...params,
    };

    const isHomeRoute =
      defaultParams.categorySlug === this.primaryCategorySlug &&
      defaultParams.page === 1 &&
      defaultParams.sortCategorySlug === null &&
      isFiltersEmpty(defaultParams.filters);

    if (defaultParams.criteria) {
      return searchRoute.build(defaultParams);
    }

    if (defaultParams.subCategorySlug) {
      return subCategoryRoute.build(defaultParams);
    }

    if (isHomeRoute) {
      return homeRoute.build({});
    }

    return categoryRoute.build(defaultParams);
  }

  public get locationBuilders(): LocationBuilders {
    return {
      home: () => homeRoute.build({}),
      forYou: () => forYouRoute.build({}),
      category: (params: CategoryPageParams) => {
        const onlyCategoryLocation = categoryRoute.build({ categorySlug: params.categorySlug });
        const fullCategoryLocation = categoryRoute.build(params);

        return params.categorySlug === this.primaryCategorySlug &&
          isLocationsSame(onlyCategoryLocation, fullCategoryLocation)
          ? homeRoute.build({})
          : categoryRoute.build(params);
      },
      subCategory: (params: SubCategoryPageParams) => subCategoryRoute.build(params),
      search: (params: SearchPageParams) => (params.criteria === '' ? homeRoute.build({}) : searchRoute.build(params)),
    };
  }

  @computed
  public get alternateCanonicalLink(): RouteLocation | undefined {
    if (this.matchedRoute.routeName === 'home') {
      return this.locationBuilders.home({});
    }

    if (this.matchedRoute.routeName === 'forYou') {
      return this.locationBuilders.forYou({});
    }

    if (this.matchedRoute.routeName === 'category') {
      const { categorySlug } = this.matchedRoute.params;
      return this.locationBuilders.category({ categorySlug });
    }

    if (this.matchedRoute.routeName === 'subCategory') {
      const { categorySlug, subCategorySlug } = this.matchedRoute.params;
      return this.locationBuilders.subCategory({ categorySlug, subCategorySlug });
    }

    return undefined;
  }

  @computed
  public get activeCategorySlug(): string | undefined {
    if (this.matchedRoute.routeName === 'home') {
      return this.primaryCategorySlug;
    }

    if (this.matchedRoute.routeName === 'category' || this.matchedRoute.routeName === 'subCategory') {
      return this.matchedRoute.params.categorySlug;
    }

    return undefined;
  }

  @computed
  public get activeSubCategorySlug(): string | undefined {
    if (this.matchedRoute.routeName === 'subCategory') {
      return this.matchedRoute.params.subCategorySlug;
    }

    return undefined;
  }

  @computed
  public get appliedFilters(): FilterCollection | null {
    if (
      this.matchedRoute.routeName === 'search' ||
      this.matchedRoute.routeName === 'category' ||
      this.matchedRoute.routeName === 'subCategory'
    ) {
      return isFiltersEmpty(this.matchedRoute.params.filters) ? null : this.matchedRoute.params.filters;
    }
    return null;
  }

  @computed
  public get appliedFiltersCount(): number {
    return this.appliedFilters ? filtersCount(this.appliedFilters) : 0;
  }

  @computed
  get currentPage(): number {
    if (this.matchedRoute.routeName === 'home' || this.matchedRoute.routeName === 'forYou') {
      return 1;
    } else {
      return this.matchedRoute.params.page;
    }
  }

  @computed
  get nextPage(): number {
    if (this.matchedRoute.routeName === 'home' || this.matchedRoute.routeName === 'forYou') {
      return 2;
    } else {
      return this.matchedRoute.params.page + 1;
    }
  }

  @computed
  get nextPageLocation(): RouteLocation {
    return this.rebuildRoute({ page: this.nextPage });
  }

  @computed
  get previousPageLocation(): RouteLocation | undefined {
    if (
      this.matchedRoute.routeName !== 'home' &&
      this.matchedRoute.routeName !== 'forYou' &&
      this.matchedRoute.params.page >= 2
    ) {
      const { page } = this.matchedRoute.params;
      return this.rebuildRoute({ page: page - 1 });
    }
  }

  @computed
  get templatePrefix(): string {
    if (this.matchedRoute.routeName === 'search') {
      return this.matchedRoute.params.criteria;
    }
    if (this.matchedRoute.routeName === 'category') {
      return this.matchedRoute.params.categorySlug;
    }
    if (this.matchedRoute.routeName === 'subCategory') {
      return this.matchedRoute.params.subCategorySlug;
    }
    return this.matchedRoute.routeName;
  }
}

export function isFiltersEmpty(filters?: FilterCollection) {
  if (!filters) {
    return true;
  }
  const { features, layouts, colors, colorStyles } = filters;
  return [...features, ...layouts, ...colors, ...colorStyles].length === 0;
}

export function filtersCount(filters?: FilterCollection): number {
  if (!filters) {
    return 0;
  }
  const { features, layouts, colors, colorStyles } = filters;
  return [...features, ...layouts, ...colors, ...colorStyles].length;
}
